/**
 * Centralized configuration for all tooth components
 * This file contains class maps and configuration for all 32 teeth across different views
 */

import { ToothConfig, ToothView, ClassMap } from '../types/tooth.types'

/**
 * Determines tooth type based on tooth number
 * Teeth 1-16: Upper arch
 * Teeth 17-32: Lower arch
 * Molars: 1-3, 14-19, 30-32
 * Premolars: 4-5, 12-13, 20-21, 28-29
 * Canines: 6, 11, 22, 27
 * Incisors: 7-10, 23-26
 */
export function getToothType(
  toothNumber: number,
): 'molar' | 'premolar' | 'canine' | 'incisor' {
  const upperMolars = [1, 2, 3, 14, 15, 16]
  const lowerMolars = [17, 18, 19, 30, 31, 32]
  const upperPremolars = [4, 5, 12, 13]
  const lowerPremolars = [20, 21, 28, 29]
  const upperCanines = [6, 11]
  const lowerCanines = [22, 27]

  if (upperMolars.includes(toothNumber) || lowerMolars.includes(toothNumber)) {
    return 'molar'
  }
  if (
    upperPremolars.includes(toothNumber) ||
    lowerPremolars.includes(toothNumber)
  ) {
    return 'premolar'
  }
  if (
    upperCanines.includes(toothNumber) ||
    lowerCanines.includes(toothNumber)
  ) {
    return 'canine'
  }
  return 'incisor'
}

/**
 * Check if tooth is in upper arch (1-16)
 */
export function isUpperTooth(toothNumber: number): boolean {
  return toothNumber >= 1 && toothNumber <= 16
}

/**
 * Buccal view class maps for molars and premolars (used for both caries and fillings)
 */
const buccalMolarPremolarCariesMap: ClassMap = {
  o: 'occlusal',
  m: 'mesial',
  d: 'distal',
  b: 'buccal',
  bs: 'buccal-surface',
  mb: ['mesio-buccal-cusp', 'mesio-buccal'],
  db: ['disto-buccal-cusp', 'disto-buccal'],
  cb: 'cervical-buccal',
  bc: 'buccal-cusp',
  p: 'palatal',
  ps: 'palatal-surface',
  pc: 'palatal-cusp',
  cp: 'cervical-palatal',
  l: 'lingual',
  ls: 'lingual-surface',
  lc: 'lingual-cusp',
  cl: 'cervical-lingual',
  mlc: 'mesio-lingual-cusp',
  dlc: 'disto-lingual-cusp',
}

/**
 * Buccal view class maps for incisors and canines (used for both caries and fillings)
 */
const buccalIncisorCariesMap: ClassMap = {
  i: 'incisal',
  m: 'mesial',
  d: 'distal',
  b: 'buccal',
  bs: 'buccal-surface',
  mb: ['mesio-buccal-cusp', 'mesio-buccal'],
  db: ['disto-buccal-cusp', 'disto-buccal'],
  cb: 'cervical-buccal',
  mifp: 'miflp',
  difp: 'diflp',
  mifl: 'miflp',
  difl: 'diflp',
  l: 'lingual',
  ls: 'lingual-surface',
  lc: 'lingual-cusp',
  cl: 'cervical-lingual',
  mlc: 'mesio-lingual-cusp',
  dlc: 'disto-lingual-cusp',
}

/**
 * Palatal view class maps (used for both caries and fillings)
 */
const palatalCariesMap: ClassMap = {
  o: 'occlusal',
  m: 'mesial',
  d: 'distal',
  p: 'palatal',
  i: 'incisal',
  mp: ['mesio-palatal', 'mesio-palatal-cusp'],
  dp: ['disto-palatal', 'disto-palatal-cusp'],
  cp: 'cervical-palatal',
  ps: 'palatal-surface',
  mifp: 'miflp',
  difp: 'diflp',
  mifl: 'miflp',
  difl: 'diflp',
  pc: 'palatal-cusp',
}

/**
 * Lingual view class maps (used for both caries and fillings)
 */
const lingualCariesMap: ClassMap = {
  o: ['occlusal'],
  m: ['mesial'],
  d: ['distal'],
  i: ['incisal'],
  l: ['lingual'],
  ls: ['lingual-surface'],
  lc: ['lingual-cusp'],
  mlc: ['mesio-lingual', 'mesio-lingual-cusp'],
  dlc: ['disto-lingual', 'disto-lingual-cusp'],
  cl: ['cervical-lingual'],
  mifp: 'miflp',
  difp: 'diflp',
  mifl: 'miflp',
  difl: 'diflp',
}

/**
 * Crown top view class maps (upper teeth, used for both caries and fillings)
 */
const crownTopCariesMap: ClassMap = {
  o: 'occlusal',
  m: 'mesial',
  d: 'distal',
  mb: ['mesio-buccal-cusp', 'mesio-buccal'],
  db: ['disto-buccal-cusp', 'disto-buccal'],
  mp: ['mesio-palatal', 'mesio-palatal-cusp'],
  dp: ['disto-palatal', 'disto-palatal-cusp'],
  b: 'buccal',
  p: 'palatal',
  bc: 'buccal-cusp',
  pc: 'palatal-cusp',
  cb: 'cervical-buccal',
  cp: 'cervical-palatal',
  bs: 'buccal-surface',
  ps: 'palatal-surface',
  mifp: 'miflp',
  difp: 'diflp',
  mifl: 'miflp',
  difl: 'diflp',
  i: 'incisal',
}

/**
 * Crown bottom view class maps (lower teeth, used for both caries and fillings)
 */
const crownBottomCariesMap: ClassMap = {
  o: 'occlusal',
  m: 'mesial',
  d: 'distal',
  b: 'buccal',
  i: 'incisal',
  l: 'lingual',
  bc: 'buccal-cusp',
  lc: 'lingual-cusp',
  mb: ['mesio-buccal-cusp', 'mesio-buccal'],
  db: ['disto-buccal-cusp', 'disto-buccal'],
  mlc: ['mesio-lingual-cusp', 'mesio-lingual'],
  dlc: ['disto-lingual-cusp', 'disto-lingual'],
  mifp: 'miflp',
  difp: 'diflp',
  mifl: 'miflp',
  difl: 'diflp',
  bs: 'buccal-surface',
  ls: 'lingual-surface',
}

/**
 * Standard inlay class maps (same for most views)
 */
const standardInlayMap: ClassMap = {
  m: 'mesial',
  d: 'distal',
  o: 'occlusal',
  i: 'incisal',
  b: 'buccal',
  bs: 'buccal-surface',
  p: 'palatal',
  ps: 'palatal-surface',
  l: 'lingual',
  ls: 'lingual-surface',
  bc: 'buccal-cusp',
  pc: 'palatal-cusp',
  lc: 'lingual-cusp',
  mb: ['mesio-buccal-cusp', 'mesio-buccal'],
  db: ['disto-buccal-cusp', 'disto-buccal'],
  mp: ['mesio-palatal', 'mesio-palatal-cusp'],
  dp: ['disto-palatal', 'disto-palatal-cusp'],
  mlc: ['mesio-lingual-cusp', 'mesio-lingual'],
  dlc: ['disto-lingual-cusp', 'disto-lingual'],
  mifp: 'miflp',
  difp: 'diflp',
  mifl: 'miflp',
  difl: 'diflp',
}

/**
 * Standard onlay class maps
 */
const standardOnlayMap: ClassMap = {
  m: 'mesial',
  d: 'distal',
  o: 'occlusal',
  i: 'incisal',
  b: 'buccal',
  bs: 'buccal-surface',
  p: 'palatal',
  ps: 'palatal-surface',
  l: 'lingual',
  ls: 'lingual-surface',
  bc: 'buccal-cusp',
  pc: 'palatal-cusp',
  lc: 'lingual-cusp',
  mb: ['mesio-buccal-cusp', 'mesio-buccal'],
  db: ['disto-buccal-cusp', 'disto-buccal'],
  mp: ['mesio-palatal', 'mesio-palatal-cusp'],
  dp: ['disto-palatal', 'disto-palatal-cusp'],
  mlc: ['mesio-lingual-cusp', 'mesio-lingual'],
  dlc: ['disto-lingual-cusp', 'disto-lingual'],
  mifp: 'miflp',
  difp: 'diflp',
  mifl: 'miflp',
  difl: 'diflp',
}

/**
 * Drifted class maps (same for all teeth)
 */
const standardDriftedMap: ClassMap = {
  b: 'buccal',
  m: 'mesial',
  d: 'distal',
  p: 'palatal',
  l: 'lingual',
}

/**
 * Get configuration for a specific tooth and view
 */
export function getToothConfig(
  toothNumber: number,
  view: ToothView,
): ToothConfig {
  const toothType = getToothType(toothNumber)

  let classMap: ClassMap

  switch (view) {
    case 'buccal':
      classMap =
        toothType === 'molar' || toothType === 'premolar'
          ? buccalMolarPremolarCariesMap
          : buccalIncisorCariesMap
      break

    case 'palatal':
      classMap = palatalCariesMap
      break

    case 'lingual':
      classMap = lingualCariesMap
      break

    case 'crown-top':
      classMap = crownTopCariesMap
      break

    case 'crown-bottom':
      classMap = crownBottomCariesMap
      break

    default:
      classMap = buccalMolarPremolarCariesMap
  }

  return {
    cariesClassMap: classMap,
    inlayClassMap: standardInlayMap,
    onlayClassMap: standardOnlayMap,
    fillingClassMap: classMap,
    driftedClassMap: standardDriftedMap,
  }
}
